export const BLOG_POSTS = [
  {
    slug: "predictive-maintenance-for-offshore-rigs",
    category: "Technology",
    title: "Predictive Maintenance for Offshore Rigs: Practical Rollout Framework",
    date: "April 2026",
    excerpt:
      "A field-level guide to moving from reactive maintenance to predictive maintenance in drilling operations without disrupting uptime.",
    content: [
      "Predictive maintenance starts with one clear target: reduce unplanned downtime on assets that directly impact drilling continuity. Most operators get better results by piloting on mud pumps, top drives, and draw works before scaling to all rotating equipment.",
      "A workable model combines condition data, maintenance history, and operational context. Rather than replacing maintenance teams, analytics should help supervisors prioritize inspection windows and parts replacement based on risk and criticality.",
      "For manpower teams, this shift creates new role expectations. Reliability technicians, instrumentation specialists, and data-aware mechanical supervisors become critical for sustained results.",
    ],
  },
  {
    slug: "closed-loop-drilling-control-readiness",
    category: "Automation",
    title: "Closed-Loop Drilling Control Readiness Checklist",
    date: "April 2026",
    excerpt:
      "How to prepare crews, controls, and governance for safe closed-loop drilling operations.",
    content: [
      "Closed-loop control can improve consistency, but readiness is the deciding factor. The first requirement is clear operating boundaries for pressure windows, vibration thresholds, and directional constraints.",
      "Teams should run supervised automation phases before full closed-loop execution. This keeps driller authority intact while validating sensor reliability and logic behavior in live conditions.",
      "Recruitment strategy should prioritize drillers and toolpushers with automation exposure, then layer targeted upskilling for control-room coordination and exception handling.",
    ],
  },
  {
    slug: "digital-rig-commissioning-playbook",
    category: "Rig Upgrades",
    title: "Digital Rig Commissioning Playbook for New Campaign Starts",
    date: "March 2026",
    excerpt:
      "A commissioning structure that aligns controls, instrumentation, and workforce readiness before first spud.",
    content: [
      "Digital commissioning must cover both equipment and people. Functional testing of integrated systems should be paired with role-based drills so crews understand how digital alarms and recommendations affect operational decisions.",
      "Campaign teams often improve startup reliability by creating a commissioning matrix across mechanical, electrical, controls, and HSE. Each critical system needs clear acceptance criteria, fallback logic, and response ownership.",
      "From a manpower perspective, mobilizing one experienced integration engineer per shift can accelerate stabilization during the first operational weeks.",
    ],
  },
  {
    slug: "manpower-planning-for-turnaround-projects",
    category: "Manpower",
    title: "Manpower Planning for Turnaround Projects in Refineries",
    date: "March 2026",
    excerpt:
      "How to build phased staffing models for refinery shutdowns and turnarounds under strict timelines.",
    content: [
      "Turnaround success depends on phase-based staffing, not static headcount. Pre-shutdown preparation, peak execution, and restart stages each require different mixes of supervision, QA/QC, and specialist technicians.",
      "Projects with lower delay risk usually define minimum role coverage at gate level, then add flex pools for high-uncertainty workpacks.",
      "Strong mobilization planning also includes accommodation, credential checks, medical readiness, and transport planning to prevent early productivity loss.",
    ],
  },
  {
    slug: "competency-matrix-for-drilling-crews",
    category: "Workforce Development",
    title: "Building a Competency Matrix for Drilling and Workover Crews",
    date: "February 2026",
    excerpt:
      "A practical template for mapping roles, certifications, and capability depth across drilling teams.",
    content: [
      "A competency matrix should do more than track certifications. It must show role readiness by operation type, equipment platform, and shift-critical tasks.",
      "The most useful matrix designs classify competency in levels such as supervised, independent, and mentor-capable. This helps operations assign crews with better balance across shifts.",
      "Linking the matrix to hiring and training plans creates a stable talent pipeline and reduces dependency on last-minute mobilization.",
    ],
  },
  {
    slug: "hse-briefing-design-for-multi-national-crews",
    category: "HSE",
    title: "HSE Briefing Design for Multi-National Crews",
    date: "February 2026",
    excerpt:
      "Designing safety communication that works across languages, cultures, and mixed experience levels.",
    content: [
      "Safety briefings are most effective when converted from policy language into task-level risk controls. Visual methods, scenario walk-throughs, and short multilingual checkpoints improve retention significantly.",
      "Supervisors should combine mandatory daily toolbox talks with weekly pattern reviews of near misses and permit deviations.",
      "Recruitment teams can strengthen HSE outcomes by screening for behavior-based safety mindset, not only technical skill and certificates.",
    ],
  },
  {
    slug: "cost-control-in-offshore-mobilization",
    category: "Operations",
    title: "Cost Control in Offshore Mobilization: What Actually Works",
    date: "January 2026",
    excerpt:
      "Reducing mobilization cost overruns through role bundling, schedule discipline, and logistics sequencing.",
    content: [
      "Mobilization budgets often drift due to sequence errors, not individual line-item inflation. Aligning visas, medical checks, certifications, and transport bookings to campaign milestones reduces idle travel costs.",
      "Some operators improve efficiency by role bundling, where suitable cross-functional personnel handle compatible operational tasks.",
      "Forecast models should include likely churn and replacement rates in critical roles so budget reserves reflect realistic campaign behavior.",
    ],
  },
  {
    slug: "oilfield-equipment-availability-strategy",
    category: "Equipment",
    title: "Oilfield Equipment Availability Strategy for Long Campaigns",
    date: "January 2026",
    excerpt:
      "Planning spare coverage and service intervals to sustain high equipment availability in remote operations.",
    content: [
      "Availability planning begins with criticality mapping: identify components where failure creates immediate NPT and define replacement or repair windows accordingly.",
      "Remote campaigns should maintain tiered inventory, with essential spares on-site and deeper reserve pools positioned at regional hubs.",
      "Operations teams benefit when equipment strategy and manpower planning are linked, ensuring qualified maintenance personnel are present where failure risk is highest.",
    ],
  },
  {
    slug: "training-roadmap-for-energy-transition-skills",
    category: "Skills",
    title: "Training Roadmap for Energy Transition Skill Requirements",
    date: "December 2025",
    excerpt:
      "How oil and gas teams can reskill for adjacent energy projects while keeping core operations strong.",
    content: [
      "Energy transition does not reduce the need for oil and gas operational rigor; it expands the skill mix. Training roadmaps should identify overlapping competencies first, then add domain-specific modules.",
      "Cross-skilling plans are strongest when they are role-segmented, with distinct pathways for field technicians, supervisors, and project engineers.",
      "Structured transition programs improve retention because they give high-potential staff a long-term growth path without leaving the organization.",
    ],
  },
  {
    slug: "crew-change-resilience-during-peak-demand",
    category: "Manpower",
    title: "Crew Change Resilience During Peak Demand Cycles",
    date: "December 2025",
    excerpt:
      "Designing backup staffing and handover systems that protect output during tight labor markets.",
    content: [
      "Peak demand periods expose weak points in crew-change planning. Projects need backup pools for high-risk roles and clearly documented handover standards between shifts and rotations.",
      "Availability forecasting should be updated weekly during high-demand windows to account for travel disruptions and delayed clearances.",
      "A resilient model combines local standby resources with internationally deployable specialists to preserve schedule integrity.",
    ],
  },
];

export const NEWS_POSTS = [
  {
    slug: "offshore-automation-adoption-accelerates-2026",
    category: "Industry News",
    title: "Offshore Automation Adoption Accelerates Across New Campaigns",
    date: "April 2026",
    excerpt:
      "Operators are increasing investment in rig automation and integrated controls to improve consistency and lower non-productive time.",
    content: [
      "Recent campaign planning cycles show stronger inclusion of automation deliverables at bid and contract stages. Automation is now treated as a core performance enabler rather than a pilot add-on.",
      "Rig teams report improved repeatability in drilling execution where control systems and directional workflows are tightly integrated.",
      "The shift is also increasing demand for personnel with hands-on automation and controls experience.",
    ],
  },
  {
    slug: "regional-rig-upgrade-programs-expand",
    category: "Fleet News",
    title: "Regional Rig Upgrade Programs Continue to Expand",
    date: "April 2026",
    excerpt:
      "Multiple markets are extending digital retrofit and upgrade programs for jack-up and land rigs.",
    content: [
      "Upgrade programs are focused on monitoring systems, power efficiency, and operational controls that can improve uptime metrics.",
      "Operators are prioritizing upgrades that can be implemented during planned maintenance windows to avoid campaign interruptions.",
      "The program pipeline signals sustained demand for commissioning engineers and multi-skilled maintenance teams.",
    ],
  },
  {
    slug: "manpower-demand-rises-for-drilling-specialists",
    category: "Workforce News",
    title: "Manpower Demand Rises for Drilling and Intervention Specialists",
    date: "March 2026",
    excerpt:
      "Hiring demand remains high for experienced drilling, well services, and HSE-critical roles.",
    content: [
      "Recruitment activity indicates stronger competition for experienced field professionals, especially for fast-mobilization assignments.",
      "Projects with structured onboarding and retention incentives are filling key vacancies faster than peers.",
      "Upskilling mid-career professionals is becoming a preferred strategy to reduce dependency on limited senior talent pools.",
    ],
  },
  {
    slug: "refinery-turnaround-activity-strengthens",
    category: "Operations News",
    title: "Refinery Turnaround Activity Strengthens in Key Corridors",
    date: "March 2026",
    excerpt:
      "Planned shutdown and turnaround programs are increasing, creating short-cycle demand for specialist crews.",
    content: [
      "Project schedules show tighter shutdown windows and stronger pressure for execution certainty.",
      "Clients are requesting bundled manpower solutions that include supervision, safety, and quality capability from day one.",
      "Advance planning for logistics and credentialing is becoming central to turnaround execution performance.",
    ],
  },
  {
    slug: "maintenance-digitalization-becomes-standard",
    category: "Technology News",
    title: "Maintenance Digitalization Becomes Standard in New O&M Contracts",
    date: "February 2026",
    excerpt:
      "Condition monitoring and digital work-order workflows are now common requirements in O&M scopes.",
    content: [
      "Digital maintenance practices are being embedded in contract KPIs, especially for uptime and response-time metrics.",
      "Teams with integrated CMMS and field inspection data are reducing avoidable repeat failures.",
      "Service providers are increasing hiring for reliability-focused maintenance profiles.",
    ],
  },
  {
    slug: "safety-performance-drives-contract-awards",
    category: "HSE News",
    title: "Safety Performance Continues to Influence Contract Awards",
    date: "February 2026",
    excerpt:
      "Clients are placing stronger weight on measurable safety culture and incident-prevention systems.",
    content: [
      "Bid evaluations increasingly include behavior-based safety frameworks, training depth, and leading safety indicators.",
      "Organizations with consistent safety outcomes are seeing stronger positioning for long-duration contracts.",
      "HSE leadership capability is becoming a differentiator alongside technical execution.",
    ],
  },
  {
    slug: "cross-border-mobilization-remains-priority",
    category: "Recruitment News",
    title: "Cross-Border Mobilization Remains a Priority Focus",
    date: "January 2026",
    excerpt:
      "International projects continue to prioritize end-to-end mobilization readiness and documentation quality.",
    content: [
      "Mobilization quality is increasingly linked to schedule reliability in international campaigns.",
      "Clients are expecting partners to manage medicals, credentials, and deployment documentation through structured workflows.",
      "Demand is growing for manpower partners with proven cross-region deployment execution.",
    ],
  },
  {
    slug: "integrated-project-delivery-models-grow",
    category: "Project News",
    title: "Integrated Project Delivery Models Gain Momentum",
    date: "January 2026",
    excerpt:
      "More operators are consolidating services and manpower under integrated execution contracts.",
    content: [
      "Integrated models can improve accountability and decision speed by reducing handoff friction between contractors.",
      "These contracts typically require stronger interface management and broader workforce planning depth.",
      "Service companies with multidisciplinary capability are increasingly preferred in bid shortlists.",
    ],
  },
  {
    slug: "regional-training-hubs-expand-capacity",
    category: "Training News",
    title: "Regional Training Hubs Expand Capacity for Energy Roles",
    date: "December 2025",
    excerpt:
      "Training ecosystem growth is helping address gaps in operational and technical role readiness.",
    content: [
      "Expanded training capacity supports faster onboarding and better competency alignment for field deployment.",
      "Programs are placing more emphasis on practical simulation and role-specific assessment.",
      "Companies that align recruitment with training partnerships are reducing time-to-deployment.",
    ],
  },
  {
    slug: "client-focus-shifts-to-operational-resilience",
    category: "Market News",
    title: "Client Priorities Shift Further Toward Operational Resilience",
    date: "December 2025",
    excerpt:
      "Across upstream and downstream projects, resilience planning is becoming a central performance expectation.",
    content: [
      "Resilience strategies include flexible staffing models, stronger preventive maintenance planning, and tighter execution governance.",
      "Operators are emphasizing continuity metrics in addition to production and cost outcomes.",
      "This trend reinforces demand for manpower partners that can combine technical depth with reliable mobilization.",
    ],
  },
];

export const getAllMediaPosts = () =>
  [...BLOG_POSTS.map((x) => ({ ...x, type: "blog" })), ...NEWS_POSTS.map((x) => ({ ...x, type: "news" }))];
